import {reportImageUploadProgress} from './ImageUploadProgress';
import {signedDownloadCache} from './SignedDownloadCache';
import {TAGS} from './TagTypes';
import {ENDPOINTS} from '../common/endpoints';
import {baseAPI} from './BaseAPI';
import {OrderGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {IOrder} from '../models/Order/IOrder';
import {DTO_STATE} from '@mybake/ui/common/dictionary';
import {fileMetadata, putSignedImage, SignedDownload, SignedUpload} from './SignedImageUpload';

const uploads = new WeakMap<File, Map<string, {ticket: SignedUpload; imageId?: string; attached?: boolean}>>();

export const imageAPI = baseAPI.injectEndpoints({
    endpoints: build => ({
        uploadImagesV2: build.mutation<null, {saved: OrderGuidDto; draft: IOrder; files: Map<string, File>}>({
            invalidatesTags: (_result, _error, {saved}) => [{type: TAGS.ORDER_V2, id: saved.id}, {type: TAGS.ORDER_V2, id: 'LIST'}, {type: TAGS.ORDER_V2, id: 'SEARCH'}],
            async queryFn({saved, draft, files}, _api, _options, query) {
                try {
                    const subOrders = (draft.subOrders ?? []).filter(sub => sub.state !== DTO_STATE.DTO_DELETED);
                    const total = subOrders.reduce((count, sub) => count + (sub.images ?? []).filter(image => image.state !== DTO_STATE.DTO_DELETED && !image.imageId && files.has(image.fileName)).length, 0);
                    let completedCount = 0;
                    reportImageUploadProgress(saved.id, 0, total);
                    for (const [index, sub] of subOrders.entries()) {
                        const target = saved.subOrders.find(value => value.id === sub.id) ?? saved.subOrders[index];
                        if (!target) throw new Error('Saved sub-order is missing');
                        for (const image of sub.images ?? []) {
                            if (image.state === DTO_STATE.DTO_DELETED || image.imageId) continue;
                            const file = files.get(image.fileName);
                            if (!file) continue;
                            const key = saved.id + '/' + target.id;
                            let sessions = uploads.get(file);
                            if (!sessions) { sessions = new Map(); uploads.set(file, sessions); }
                            let session = sessions.get(key);
                            if (session?.attached) { reportImageUploadProgress(saved.id, ++completedCount, total); continue; }
                            if (!session || Date.parse(session.ticket.expiresAt) <= Date.now()) {
                                const ticket = await query({url: ENDPOINTS.IMAGES.V2 + '/upload-url', method: 'POST', body: {orderId: saved.id, ...fileMetadata(file)}});
                                if (ticket.error) return {error: ticket.error};
                                session = {ticket: ticket.data as SignedUpload};
                                sessions.set(key, session);
                            }
                            if (!session.imageId) {
                                await putSignedImage(session.ticket, file);
                                const completed = await query({url: ENDPOINTS.IMAGES.V2 + '/complete', method: 'POST', body: {uploadToken: session.ticket.uploadToken}});
                                if (completed.error) {
                                    if ((completed.error.data as {code?: string} | undefined)?.code === 'image.upload.invalid') sessions.delete(key);
                                    return {error: completed.error};
                                }
                                session.imageId = (completed.data as {id: string}).id;
                            }
                            const attached = await query({url: 'Orders/v2/' + saved.id + '/sub-orders/' + target.id + '/images/' + session.imageId + '?isResult=' + Boolean(image.isResult ?? image.isResultImage), method: 'POST'});
                            if (attached.error) return {error: attached.error};
                            session.attached = true;
                            reportImageUploadProgress(saved.id, ++completedCount, total);
                        }
                    }
                    return {data: null};
                } catch (error) {
                    return {error: {status: 'CUSTOM_ERROR', error: error instanceof Error ? error.message : String(error)}};
                }
            },
        }),
    }),
});

export const fetchImageV2 = async (imageId: string): Promise<{imageUrl: string; type: string; blob?: Blob; expiresAt?: string; error: string; problem?: unknown}> => {
    try {
        const download = await signedDownloadCache.get('image:' + imageId, async () => {
            const response = await fetch(ENDPOINTS.BASE_API_URL + ENDPOINTS.IMAGES.V2 + '/' + encodeURIComponent(imageId) + '/download-url', {credentials: 'include'});
            if (!response.ok) {
                const data = await response.json();
                if (response.status === 401) window.dispatchEvent(new CustomEvent('bakery:unauthorized', {detail: data}));
                throw {status: response.status, data};
            }
            return await response.json() as SignedDownload;
        });
        return {imageUrl: download.url, expiresAt: download.expiresAt, type: '', error: ''};
    } catch (error) {
        return {imageUrl: '', type: '', error: error instanceof Error ? error.message : 'Image request failed', problem: error};
    }
};
export const fetchImage = async (_fileName: string) => ({imageUrl: '', type: '', blob: undefined as Blob | undefined, error: 'Image GUID is required', problem: undefined});
